import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import queryString from 'query-string';

import {
  Form as SemanticForm,
  Divider,
  Grid as SemanticGrid,
} from 'semantic-ui-react';

import { ErrorHandler, captureException } from '@playbookux/playbook-web-core';
import { withTranslation } from '../../../core/i18n';

import {
  BlueStrip,
  BottomButton,
  Container,
  FullWidth,
  Loader,
  Logo,
  MainContainer,
  Subtitle,
  SurveyForm,
  Title,
} from './styles';

import { QuestionsComponents } from '../../../core/components/questions-components';

import { parseUrl } from './helper';
import withIntercom from '../../../core/components/with-intercom';
import { SCREENER_SESSION_STATUS } from '../../../core/constants/screener-sessions';
import TestTypes from '../../../core/constants/testTypes';

import {
  authenticateWithScreener,
  fetchCurrentScreenerSession,
  resetScreener,
  saveScreenerAnswers,
  verifyCurrentAnswer,
  setScreenerSessionAnswers,
  setScreenerSessionCurrentQuestion,
  setScreenerSessionNextQuestion,
  setScreenerSessionQuestions,
  setTestSession as setTreeTestSession,
} from '../../../core/actions';
import { handleRedirectSession } from '../../utils/handleRedirectSession';

const ScreenerSession = ({
  history = {},
  location = {},
  onAuthenticateWithScreener,
  onFetchCurrentScreenerSession,
  onResetScreener,
  onSaveScreenerAnswers,
  handleCurrentAnswer,
  onSetAnswers,
  onSetCurrentQuestion,
  onSetNextQuestion,
  onSetQuestions,
  onSetTreeTestSession,
  phrases,
  state,
  authentication,
}) => {
  const {
    currentQuestion = {},
    nextQuestion = {},
    isLastQuestion,
    answers = {},
  } = state;

  const search = _.get(location, 'search', {});
  const isCompleted = !_.isEmpty(answers) && !_.isEmpty(answers[currentQuestion.id]) && isLastQuestion;
  const {
    sessionId,
    token,
    requestedFrom,
    errorMessage,
  } = parseUrl(search);

  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState(null);
  const [isTokenEmpty, setIsTokenEmpty] = useState(null);
  const [errorField, setErrorField] = useState(false);
  const [project, setProject] = useState({});
  const [segment, setSegment] = useState({});
  const [tester, setTester] = useState({});

  const {
    testType,
    participantsSource,
    disqualifyRedirectUrl,
    hasMrId,
  } = project;
  const { mrId: mrIdTester } = tester;

  /**
   * Save the answers and redirect to a new page.
   */
  const handleSubmit = useCallback(async () => {
    try {
      setIsLoading(true);

      const { segmentId } = segment;

      const { lang, cintid, tk: mrid } = queryString.parse(search);

      const hasEmptyAnswers = _.isEmpty(answers) || Object.values(answers).some((answer) => _.isEmpty(answer.value));

      if (!hasEmptyAnswers && !errorField) {
        const result = await onSaveScreenerAnswers(answers, cintid, mrid);

        // After submitting and saving answers on database, clear answers.
        onResetScreener();

        if (result.errors) {
          history.push({ pathname: '/screener-session/error', state: { errors: result.errors } });
        } else if (result === SCREENER_SESSION_STATUS.OUTDATED) {
          const outdatedData = {
            recruitParticipantToken: segment.recruitParticipantToken,
            user: tester.testerUser,
          };

          history.push({ pathname: '/screener-session/outdated', state: outdatedData });
        } else if (result === 'disqualified') {
          if (!disqualifyRedirectUrl) {
            history.push(`/screener-session/disqualified?lang=${lang}`);
          } else {
            handleRedirectSession(disqualifyRedirectUrl, hasMrId, mrIdTester);
          }
        } else if (testType !== TestTypes.MODERATED) {
          // Set the values of the current test_session id in the redux.
          onSetTreeTestSession(result);
          if (search.includes('guest=true') && search.includes('demographics-survey=true')) {
            history.push(`/demographics-survey?session-id=${result.id}&testType=${testType}`);
            return;
          }

          if (search.includes('guest=true') && !search.includes('demographics-survey=true') && testType === TestTypes.UNMODERATED) {
            const newSearch = queryString.stringify({ 'session-id': result.id }, { sort: false });
            history.push({ pathname: '/unmoderated-session', search: newSearch });
            return;
          }

          if (testType === TestTypes.UNMODERATED) {
            const newSearch = queryString.stringify({ 'session-id': result.id });
            history.push({ pathname: '/unmoderated-session', search: newSearch });
            return;
          }

          history.push(`/welcome?session-id=${result.id}`);
        } else if (result.authenticationToken) {
          const { id } = result;

          const newSearch = queryString.stringify({ 'session-id': id }, { sort: false });

          if (search.includes('guest=true') && search.includes('demographics-survey=true')) {
            history.push(`/demographics-survey?session-id=${result.id}&testType=${testType}`);
          }

          history.push({ pathname: '/unmoderated-session', search: newSearch });
        } else if (testType === TestTypes.MODERATED && !search.includes('guest=true')) {
          const newSearch = queryString.stringify({ segment_id: segmentId, participants_source: participantsSource });
          history.push({ pathname: '/schedule', search: newSearch });
        } else {
          history.push(`/screener-session/success?lang=${lang}`);
        }
      } else {
        setErrorField(true);
      }
    } catch (err) {
      setError('Failure to send the answers');
      // eslint-disable-next-line no-console
      console.error('failed to save', err);
      captureException('HANDLE_SUBMIT_SCREENER_FAILED', err);
    } finally {
      setIsLoading(false);
    }
  }, [project, search, answers, errorField, onSaveScreenerAnswers, onResetScreener, history, onSetTreeTestSession]);

  /**
     * @description Authenticate screener session
     */
  const handleAuthenticateWithScreener = useCallback(async () => {
    let accessToken = authentication?.token || null;
    try {
      setIsLoading(true);
      setLoadingMessage('Authenticating with Playbook...');
      setError(null);

      if (!accessToken || requestedFrom === 'admin') {
        const resp = await onAuthenticateWithScreener(sessionId, token, true);
        accessToken = resp?.accessToken || null;
      }

      setIsTokenEmpty(accessToken);
    } catch (err) {
      setIsLoading(false);
      setError('Invalid link. Try to open a Playbook email link again.');
    } finally {
      setIsLoading(false);
    }
    return accessToken;
  }, [authentication, requestedFrom, onAuthenticateWithScreener, sessionId, token]);

  /**
   * @description Get the screener questions
   * @param {string} sessionId get through URL querystring
   */
  const handleGetQuestions = useCallback(async (accessToken) => {
    try {
      setLoadingMessage('Obtaining questions...');

      const {
        tester: testerResponse, segment: segmentSession, project: projectSession, questions,
      } = await onFetchCurrentScreenerSession(sessionId, accessToken);

      if (testerResponse.status !== 'pending') {
        setIsLoading(false);
        setLoadingMessage('');
        setError('You have already answered this screener survey.');
      } else {
        setProject({
          device: segmentSession.device,
          projectId: projectSession.id,
          testType: projectSession.testType,
          welcomeTitle: projectSession.welcomeTitle,
          welcomeContent: projectSession.welcomeContent,
          participantsSource: projectSession.participantsSource,
          disqualifyRedirectUrl: projectSession.disqualifyRedirectUrl,
          hasMrId: projectSession.hasMrId,
        });
        setTester({
          mrId: testerResponse.mrId,
          testerUser: testerResponse.user,
        });
        setSegment({
          segmentId: segmentSession.id,
          recruitParticipantToken: segmentSession.recruitParticipantToken?.token,
        });

        setIsLoading(false);
        setLoadingMessage('');

        onSetQuestions(questions, 'questionOrder', false);
      }
    } catch (err) {
      setIsLoading(false);
      setError(phrases.error_getting_screener_survey);
    }
  }, [onFetchCurrentScreenerSession, sessionId, onSetQuestions, phrases]);

  const asyncInitialValues = useCallback(async () => {
    try {
      const accessToken = await handleAuthenticateWithScreener();

      await handleGetQuestions(accessToken);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('failed to mount component', err);
    }
  }, [handleAuthenticateWithScreener, handleGetQuestions]);

  /**
   * @description Handle submit button
   */
  const handleSubmitQuestions = async (event) => {
    if (!isLoading && !error && !errorField && isCompleted) {
      handleSubmit(event);
    } else {
      setErrorField(true);
    }
  };

  /**
   * Every time the user change to a new question the URL is updated with the id of the current Question.
   */
  const handleChangeUrl = useCallback((questionId) => {
    if (!_.isEmpty(search) && !_.isEmpty(location) && !_.isEmpty(questionId)) {
      const {
        lang: langUrl,
        'session-id': sessionIdUrl,
        token: tokenUrl,
        guest,
        'demographics-survey': demographicsSurvey,
        cintid,
        tk,
      } = queryString.parse(search);

      const newSearch = queryString.stringify({
        'session-id': sessionIdUrl,
        token: tokenUrl,
        lang: langUrl,
        questionId,
        guest,
        'demographics-survey': demographicsSurvey,
        cintid,
        tk,
      }, { sort: false });

      history.push({
        pathname: location.pathname,
        search: newSearch,
      });
    }
  }, [history, location, search]);

  /**
   * Call the next question from the reducer.
   */
  const handleNextQuestion = async () => {
    if (
      !isLastQuestion
      && !errorField
      && !_.isEmpty(answers)
      && !_.isEmpty(answers[currentQuestion.id])
      && !_.isEmpty(answers[currentQuestion.id].value)
    ) {
      setIsLoading(true);
      const { lang, cintid, tk: mrid } = queryString.parse(search);
      const result = await handleCurrentAnswer(answers[currentQuestion.id]);

      if (result === 'disqualified') {
        await onSaveScreenerAnswers(answers, cintid, mrid);
        onResetScreener();

        if (!disqualifyRedirectUrl) {
          history.push(`/screener-session/disqualified?lang=${lang}`);
        } else {
          handleRedirectSession(disqualifyRedirectUrl, hasMrId, mrIdTester);
        }
      } else if (result === SCREENER_SESSION_STATUS.OUTDATED) {
        const outdatedData = {
          recruitParticipantToken: segment.recruitParticipantToken,
          user: tester.testerUser,
        };

        await onSaveScreenerAnswers(answers, cintid, mrid);
        onResetScreener();
        history.push({ pathname: '/screener-session/outdated', state: outdatedData });
      } else {
        setIsLoading(false);
        handleChangeUrl(nextQuestion.id);
        onSetNextQuestion(currentQuestion);
      }
    } else {
      setErrorField(true);
    }
  };

  /**
   * Save the selected answers of the user.
   * @param {*} value Values of the answers
   */
  const handleChange = (value) => {
    const questionType = _.get(currentQuestion, 'questionType', '');
    if (questionType !== 'multiple_select' && (_.isEmpty(value) || !value)) {
      setErrorField(true);
    } else {
      setErrorField(false);
      const newValues = { [currentQuestion.id]: { value } };
      onSetAnswers(newValues);
    }
  };

  /**
   * Used to authenticate user and get questions.
  */
  useEffect(() => {
    if (!_.isEmpty(phrases) && !isTokenEmpty) {
      asyncInitialValues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTokenEmpty, phrases]);

  /**
   * Used to add the param 'questionId' to the URL if it is the first time the user access the page.
   * Or to get the current question set in the URL.
   */
  useEffect(() => {
    if (!_.isEmpty(search) && !isLoading && !error) {
      const { questionId } = queryString.parse(search);
      if (!questionId || (questionId && questionId !== currentQuestion.id)) {
        handleChangeUrl(currentQuestion.id);
      } else {
        onSetCurrentQuestion(questionId);
      }
    }
  }, [currentQuestion.id, error, handleChangeUrl, isLoading, onSetCurrentQuestion, search]);

  if (isLoading && _.isEmpty(currentQuestion)) {
    return (
      <Container>
        <Loader
          size="huge"
          content={loadingMessage}
          data-loader="screener-session-loader"
        />
      </Container>
    );
  }

  if (error || errorMessage) {
    return (
      <Container>
        <ErrorHandler
          error={{ message: error || errorMessage }}
          defaultMessage={error || errorMessage}
        />
      </Container>
    );
  }

  return (
    <Container>
      <Logo src="/images/playbook-logo.png" alt="Playbook" />

      <BlueStrip />

      <MainContainer>
        <Title>{phrases.screener_header}</Title>
        <Subtitle>
          {phrases.take_screener_message}
        </Subtitle>
        <Divider />
        <SurveyForm>
          <SemanticForm as="div">
            <QuestionsComponents
              question={currentQuestion}
              placeholder="Type your answer"
              onChange={handleChange}
              typeQuestion={_.get(currentQuestion, 'questionType', '')}
              errorField={errorField}
              selectedValue={answers ? answers[currentQuestion.id] : {}}
              isScreener
            />

            <FullWidth>
              <SemanticGrid centered>
                {isLastQuestion && (
                  <BottomButton
                    loading={isLoading}
                    disabled={isLoading || errorField}
                    type="submit"
                    onClick={handleSubmitQuestions}
                  >
                    {phrases.screener_submit_button}
                  </BottomButton>
                )}

                {!isLastQuestion && (
                  <BottomButton
                    loading={isLoading}
                    disabled={isLoading || errorField}
                    type="button"
                    onClick={handleNextQuestion}
                  >
                    NEXT
                  </BottomButton>
                )}
              </SemanticGrid>
            </FullWidth>
          </SemanticForm>
        </SurveyForm>
      </MainContainer>
    </Container>
  );
};

ScreenerSession.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  onAuthenticateWithScreener: PropTypes.func.isRequired,
  onFetchCurrentScreenerSession: PropTypes.func.isRequired,
  phrases: PropTypes.shape({
    error_getting_screener_survey: PropTypes.string,
    screener_header: PropTypes.string,
    take_screener_message: PropTypes.string,
    screener_submit_button: PropTypes.string,
  }).isRequired,
  state: PropTypes.shape({
    currentQuestion: PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
    nextQuestion: PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
    isLastQuestion: PropTypes.bool.isRequired,
    answers: PropTypes.shape({}).isRequired,
  }).isRequired,
  onResetScreener: PropTypes.func.isRequired,
  onSetCurrentQuestion: PropTypes.func.isRequired,
  onSetNextQuestion: PropTypes.func.isRequired,
  onSaveScreenerAnswers: PropTypes.func.isRequired,
  handleCurrentAnswer: PropTypes.func.isRequired,
  onSetAnswers: PropTypes.func.isRequired,
  onSetQuestions: PropTypes.func.isRequired,
  onSetTreeTestSession: PropTypes.func.isRequired,
  authentication: PropTypes.shape().isRequired,
};

const mapStateToProps = ({ authentication, screenerSessionAnswers }) => ({
  authentication,
  state: screenerSessionAnswers,
});

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    onAuthenticateWithScreener: authenticateWithScreener,
    onFetchCurrentScreenerSession: fetchCurrentScreenerSession,
    onResetScreener: resetScreener,
    onSaveScreenerAnswers: saveScreenerAnswers,
    handleCurrentAnswer: verifyCurrentAnswer,
    onSetAnswers: setScreenerSessionAnswers,
    onSetCurrentQuestion: setScreenerSessionCurrentQuestion,
    onSetNextQuestion: setScreenerSessionNextQuestion,
    onSetQuestions: setScreenerSessionQuestions,
    onSetTreeTestSession: setTreeTestSession,
  },
  dispatch,
);

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withTranslation,
  withIntercom,
)(ScreenerSession);
